/**
 * Sections arrive as you scroll to them.
 *
 * The single cheapest thing that makes a page feel alive rather than printed. Every
 * section fades up a few pixels as it enters the viewport, once, and then stays put.
 *
 * ## Content is never hidden by CSS alone
 *
 * The obvious implementation — a `.reveal` class in the stylesheet that starts at
 * `opacity: 0`, with JavaScript adding `.is-visible` — hides the entire page from anyone
 * whose JavaScript did not run: a crawler that does not execute it, a browser that failed
 * to fetch the bundle, an extension that broke it. The page would be blank and nobody
 * would know why.
 *
 * So the hidden state is applied BY JavaScript, not by the stylesheet. `data-reveal` is
 * only ever set from here. No script, no attribute, no hiding — the page renders exactly
 * as it did before this file existed.
 *
 * ## Reduced motion
 *
 * Honoured by doing nothing at all: no attribute is set, so there is no transition and no
 * movement. The content is simply there, which is what the preference asks for.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** Elements start hidden only once we know we can animate them back into view. */
const PENDING = 'pending';
const VISIBLE = 'in';

export function startReveal(root: ParentNode = document): () => void {
  // Both of these are reasons to leave the page exactly as it is.
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  if (reduced || typeof IntersectionObserver !== 'function') return () => undefined;

  // Every page section, rather than an opt-in attribute on twenty-four components. The
  // marquee is skipped: it is `aria-hidden` decoration that is already moving, and
  // fading it in as well is two animations arguing.
  const targets = [...root.querySelectorAll<HTMLElement>('section.section')].filter(
    (el) => !el.dataset.reveal && !el.hasAttribute('aria-hidden'),
  );
  if (targets.length === 0) return () => undefined;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        (entry.target as HTMLElement).dataset.reveal = VISIBLE;
        observer.unobserve(entry.target);
      }
    },
    // A negative bottom margin so a section starts arriving slightly before its top edge
    // reaches the fold — it is in place by the time it is being read, rather than
    // animating under the reader's eye.
    { rootMargin: '0px 0px -12% 0px', threshold: 0.02 },
  );

  for (const el of targets) {
    const box = el.getBoundingClientRect();

    // Anything already on screen at load — the hero, above all — is shown immediately.
    // Animating it would mean the first thing a visitor sees is the page assembling
    // itself, which reads as slowness, not polish.
    if (box.top < window.innerHeight * 0.9) {
      el.dataset.reveal = VISIBLE;
      continue;
    }

    el.dataset.reveal = PENDING;
    observer.observe(el);
  }

  return () => observer.disconnect();
}

/* -------------------------------------------------------------------------- */

/**
 * Run the reveal on every route, and again when content arrives.
 *
 * The second half matters more than the first. Sections are rendered from data fetched
 * after the route mounts, so a single pass on mount would find an empty page and mark
 * nothing — the effect would appear to work on a warm cache and do nothing on a cold
 * one. A `MutationObserver` re-scans as sections appear; `startReveal` skips anything it
 * has already marked, so re-running is free.
 */
export function useReveal(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    let disconnect = startReveal();

    const main = document.querySelector('main');
    if (!main || typeof MutationObserver !== 'function') return () => disconnect();

    let frame = 0;
    const watcher = new MutationObserver(() => {
      // Coalesced: a React render can fire dozens of mutations for one section.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        disconnect();
        disconnect = startReveal();
      });
    });
    watcher.observe(main, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(frame);
      watcher.disconnect();
      disconnect();
    };
  }, [pathname]);
}
